import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';

import {useSelector} from 'react-redux';

import {isNotNil, uniq, uniqBy} from 'ramda';

import {
  selectTenant,
  useGetAuctionServerListQuery,
  useSourcingGetSourcingServersQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {ALLOWED_SELLER_TYPE_KEYS} from '../../../../../../../constants/allowedSellerTypeKeys';
import {UNSUPPORTED_ORIGIN_COUNTRY_CODES} from '../../../../../../../constants/unsupportedOriginCountryCodes';
import {useApiEnums} from '../../../../../../../hooks/useApiEnums';
import {SourcingType, useSourcingType} from '../../../../../../../hooks/useSourcingType';
import {AuctionTypeFilter} from '../../../../../../../types/AuctionTypeFilter';
import {collapseSelectedFeatureValues} from '../../../../../../../utils/collapseSelectedFeatureValues';
import {getAuctionTypePresentation} from '../../../../../../../utils/getAuctionTypePresentation';
import {useMakeModelFilter} from '../../../../../hooks/useMakeModelFilter';
import {useTrimLevelOptions} from '../../../../../hooks/useTrimLevelOptions';
import {CarStatusFilter} from '../../../../../types/CarStatusFilter';
import {VehicleCategoryFilter} from '../../../../../types/VehicleCategoryFilter';
import {getAppliedVehicleCategory} from '../../../../../utils/getAppliedVehicleCategory';
import {getBodyTypeOptions} from '../../../../../utils/getBodyTypeOptions';
import {FilterFieldName} from '../../../../FilterEditor/utils/getFilterSections';
import {isFilterAvailable} from '../../../../FilterEditor/utils/isFilterAvailable';
import {useFilters} from '../../../../FiltersContext/FiltersContext';
import {allowedCountryCodes} from '../../../constants/AllowedCountryCodes';
import {filterSpecificationOptions} from '../../FilterSpecification/utils/filterSpecificationOptions';
import {useChangeVehicleCategory} from '../../FilterVehicleCategory/hooks/useChangeVehicleCategory';

function valuesFor<Value extends string>(
  field: FilterFieldName,
  options: {value: Value; label: string}[],
  select: (value: Value) => void
) {
  return options.map((option) => ({
    value: `${field}:${option.value}`,
    label: option.label,
    field,
    select: () => select(option.value),
  }));
}

export function useFilterSearchValues() {
  const {filters, onUpdateFilters} = useFilters();
  const source = useSourcingType();
  const {getEnum, isLoading} = useApiEnums({flat: true});
  const {getEnum: getTreeEnum} = useApiEnums();
  const catalogue = useMakeModelFilter();
  const trimLevels = useTrimLevelOptions();
  const changeCategory = useChangeVehicleCategory();
  const {data: {country: countryCode} = {}} = useSelector(selectTenant);
  const {data: sellerServers, isLoading: loadingSellers} = useSourcingGetSourcingServersQuery(
    isNotNil(countryCode) && source === SourcingType.CLASSIFIEDS ? {countryCode} : skipToken
  );
  const {data: auctionServers, isLoading: loadingAuctions} = useGetAuctionServerListQuery(
    undefined,
    {skip: source !== SourcingType.AUCTIONS}
  );
  const categoryLabels = {
    [VehicleCategoryFilter.CAR]: i18n.t('entity.sourcing.filters.labels.vehicleCategoryCar'),
    [VehicleCategoryFilter.VAN]: i18n.t('entity.sourcing.filters.labels.vehicleCategoryVan'),
  };
  const bodyStyles = getEnum('car_style');
  const bodyTypes = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS)
    ? getBodyTypeOptions(
        bodyStyles,
        getAppliedVehicleCategory(filters.vehicleCategory, source),
        categoryLabels
      ).flatMap((group) => group.options)
    : bodyStyles;
  const enumGroups = [
    {
      field: 'sellerCountry',
      options: getEnum('country').filter((item) => allowedCountryCodes.includes(item.key)),
    },
    {
      field: 'originCountry',
      options: getEnum('country').filter(
        (item) => !UNSUPPORTED_ORIGIN_COUNTRY_CODES.includes(item.key)
      ),
    },
    {
      field: 'sellerType',
      options: getEnum('seller_type').filter((item) => ALLOWED_SELLER_TYPE_KEYS.includes(item.key)),
    },
    {
      field: 'sellerServer',
      options: sellerServers?.map((item) => ({value: item.code, label: item.name})) ?? [],
    },
    {
      field: 'auctionServer',
      options: auctionServers?.map((item) => ({value: item.id, label: item.name})) ?? [],
    },
    {field: 'fuelType', options: getEnum('fuel_type')},
    {
      field: 'transmission',
      options: getEnum('transmission').filter(
        (item) => !filterSpecificationOptions.ignoredTransmissions.includes(item.key)
      ),
    },
    {field: 'bodyType', options: bodyTypes},
    {
      field: 'drive',
      options: getEnum('drive').filter(
        (item) => !filterSpecificationOptions.ignoredDrives.includes(item.key)
      ),
    },
    {
      field: 'doorCount',
      options: getEnum('door_count').filter((item) =>
        filterSpecificationOptions.allowedDoorCount.includes(item.key)
      ),
    },
    {field: 'interiorMaterial', options: getEnum('interior_material')},
    {field: 'vehicleLengthCategory', options: getEnum('dimensions_length')},
    {field: 'vehicleHeightCategory', options: getEnum('dimensions_height')},
    {
      field: 'trimLevels',
      options: trimLevels.options,
    },
  ] as const;
  const values = [
    ...enumGroups.flatMap(({field, options}) =>
      valuesFor(field, options, (value) =>
        onUpdateFilters(field, (current) => uniq([...(current ?? []), value]))
      )
    ),
    ...valuesFor(
      'make',
      Object.entries(catalogue.makes ?? {}).map(([value, item]) => ({value, label: item.label})),
      catalogue.selectMake
    ),
    ...valuesFor(
      'model',
      Object.entries(catalogue.modelFamilies ?? {}).map(([value, item]) => ({
        value,
        label: `${item.label} · ${i18n.t('page.filters.labels.modelFamilies')}`,
      })),
      catalogue.selectModelFamily
    ),
    ...valuesFor(
      'model',
      Object.entries(catalogue.models ?? {}).map(([value, item]) => ({
        value,
        label: item.label,
      })),
      catalogue.selectModel
    ),
    ...valuesFor('features', getEnum('feature'), (value) =>
      onUpdateFilters('features', (current) =>
        collapseSelectedFeatureValues(uniq([...(current ?? []), value]), getTreeEnum('feature'))
      )
    ),
    ...valuesFor('selectedColors', getEnum('body_color'), (value) =>
      onUpdateFilters('selectedColors', (current) =>
        uniqBy(
          (item) => item.value,
          [...(current ?? []), ...getEnum('body_color').filter((item) => item.value === value)]
        )
      )
    ),
    ...valuesFor(
      'auctionType',
      Object.values(AuctionTypeFilter).map((value) => ({
        value,
        label: getAuctionTypePresentation(value)?.label ?? value,
      })),
      (value) => onUpdateFilters('auctionType', (current) => uniq([...(current ?? []), value]))
    ),
    ...valuesFor(
      'carStatus',
      [
        {value: CarStatusFilter.ACTIVE, label: i18n.t('entity.auction.labels.active')},
        {value: CarStatusFilter.ENDED, label: i18n.t('entity.auction.labels.ended')},
      ],
      (value) => onUpdateFilters('carStatus', (current) => uniq([...(current ?? []), value]))
    ),
    ...valuesFor(
      'vehicleCategory',
      Object.values(VehicleCategoryFilter).map((value) => ({value, label: categoryLabels[value]})),
      changeCategory
    ),
  ].filter((item) => isFilterAvailable(item.field, filters, source));

  return {
    values,
    isLoading:
      isLoading || catalogue.isLoading || loadingSellers || loadingAuctions || trimLevels.isLoading,
  };
}
